// Comprehensive UK city data for geo-targeted SEO landing pages
// Cities are grouped by tier: major cities get fully custom content,
// others get high-quality templated content from structured data.

// Optional per-city fields that make a page genuinely local. Each section only
// renders when its field is present, so fill them in from real bookings only:
//   pricing:       { from: "£450", note: "2 dhol players, 1 hour, central Birmingham" }
//   coverageNotes: "Free travel within the M25; venues beyond Heathrow add £40..."
//   venuesPlayed:  [{ name: "Grand Sapphire, Croydon", year: 2024, event: "Baraat & reception" }]
//   reviews:       [{ quote: "...", author: "Simran & Gurpreet", event: "Wedding in Solihull, 2024" }]
//   gallery:       [{ type: "image", src: "/media/birmingham/icc.jpg", alt: "Baraat at ICC Birmingham" },
//                   { type: "video", src: "https://www.youtube.com/embed/<id>", title: "Vaisakhi in Handsworth" }]
const cityDatabase = [
  // ============================================================
  // TIER 1 - Major Cities (fully custom copy)
  // ============================================================
  {
    slug: "london",
    name: "London",
    region: "Greater London",
    county: "Greater London",
    tier: 1,
    areas: ["Central London", "East London", "West London", "North London", "South London", "Southall", "Wembley", "Hounslow", "Ilford", "Croydon", "Harrow", "Ealing", "Newham", "Brent", "Redbridge"],
    venues: ["The Dorchester", "Claridge's", "The Savoy", "Hilton Park Lane", "Meridian Grand", "Regency Banqueting Suite", "Grand Sapphire Hotel", "Grosvenor House"],
    nearby: ["Watford", "Slough", "Croydon", "Reading", "Luton"],
    testimonial: {
      quote: "Imperial Drummers made our London wedding absolutely spectacular. The baraat entrance at the Dorchester was unforgettable!",
      author: "Priya & Amit",
      event: "Wedding at The Dorchester, 2024"
    },
    customH1: "Dhol Players in London - #1 Rated Dhol Drummers for Hire",
    customMetaTitle: "Dhol Players in London | #1 UK Dhol Drummers for Weddings & Events",
    customMetaDescription: "Book dhol players in London for weddings, baraats, mehndis & Vaisakhi. Punjabi dhol drummers in Southall, Wembley, Ilford & across Greater London.",
    geo: { lat: "51.5074", lng: "-0.1278" },
    postalCode: "W1",
    customIntro: "Looking for dhol players in London? Imperial Drummers is the capital's premier dhol entertainment company, providing professional Punjabi dhol drummers for Asian weddings, Indian weddings, corporate events, and celebrations across Greater London and the Home Counties.",
    customContent: "As London's trusted dhol entertainment provider, we understand the diverse cultural requirements of the capital's Asian communities. From traditional Punjabi weddings in Southall to luxury celebrations in Mayfair, our experienced dhol players deliver authentic performances tailored to your event. We regularly perform at London's most prestigious venues and have built strong relationships with wedding planners across the city."
  },
  {
    slug: "birmingham",
    name: "Birmingham",
    region: "West Midlands",
    county: "West Midlands",
    tier: 1,
    areas: ["Birmingham City Centre", "Solihull", "Handsworth", "Sparkbrook", "Sparkhill", "Small Heath", "Edgbaston", "Moseley", "Sutton Coldfield", "Hall Green", "Alum Rock", "Lozells"],
    venues: ["ICC Birmingham", "The Vox Conference Centre", "Edgbaston Stadium", "Hyatt Regency Birmingham", "The Grand Hotel Birmingham", "Aston Villa FC"],
    nearby: ["Wolverhampton", "Coventry", "Walsall", "Dudley", "Solihull"],
    testimonial: {
      quote: "The dhol players from Imperial Drummers brought incredible energy to our Birmingham wedding. All our guests were dancing!",
      author: "Simran & Gurpreet",
      event: "Wedding in Solihull, 2024"
    },
    customIntro: "Searching for dhol players in Birmingham? Imperial Drummers serves the West Midlands with professional Punjabi dhol entertainment for Asian weddings, Indian weddings, and cultural celebrations. We cover Birmingham, Wolverhampton, Coventry, and the entire West Midlands region.",
    customContent: "Birmingham has one of the UK's largest South Asian populations, and our dhol players understand the traditions and expectations of the local community. Whether it's a Sikh wedding in Handsworth or a Hindu celebration in Solihull, we deliver authentic performances that honour your heritage. Our drummers are experienced with the region's popular wedding venues and work closely with local planners to ensure seamless entertainment."
  },
  {
    slug: "manchester",
    name: "Manchester",
    region: "Greater Manchester",
    county: "Greater Manchester",
    tier: 1,
    areas: ["Manchester City Centre", "Rusholme", "Cheetham Hill", "Longsight", "Levenshulme", "Bolton", "Stockport", "Oldham", "Rochdale", "Salford", "Trafford", "Bury"],
    venues: ["The Lowry Hotel", "Hilton Manchester Deansgate", "Manchester Central", "Old Trafford", "The Midland Hotel", "Victoria Warehouse"],
    nearby: ["Bolton", "Oldham", "Stockport", "Rochdale", "Macclesfield"],
    testimonial: {
      quote: "Absolutely phenomenal! The dhol drummers created the perfect atmosphere for our Manchester wedding reception.",
      author: "Anika & Raj",
      event: "Wedding at The Lowry, 2024"
    },
    customIntro: "Need dhol players in Manchester? Imperial Drummers is the North West's leading dhol entertainment company, providing professional Punjabi dhol drummers for weddings and events across Greater Manchester, Lancashire, and Cheshire.",
    customContent: "Manchester's vibrant South Asian community deserves the best dhol entertainment. Our experienced drummers regularly perform at venues across Greater Manchester, from intimate family gatherings to grand celebrations at the city's finest hotels. We understand the cultural diversity of the North West and tailor every performance to match your vision."
  },
  {
    slug: "leicester",
    name: "Leicester",
    region: "East Midlands",
    county: "Leicestershire",
    tier: 1,
    areas: ["Leicester City Centre", "Belgrave", "Rushey Mead", "Evington", "Oadby", "Wigston", "Loughborough", "Hinckley", "Melton Mowbray", "Market Harborough"],
    venues: ["Leicester Marriott Hotel", "Athena Leicester", "The City Rooms", "Winstanley House", "Belgrave Neighbourhood Centre"],
    nearby: ["Nottingham", "Derby", "Northampton", "Coventry", "Peterborough"],
    testimonial: {
      quote: "Imperial Drummers understood exactly what we wanted for our Belgrave wedding. Truly authentic Punjabi dhol!",
      author: "Harpreet & Mandeep",
      event: "Wedding in Belgrave, 2024"
    },
    customIntro: "Looking for dhol players in Leicester? Imperial Drummers serves the East Midlands with professional Punjabi dhol entertainment for Asian weddings and cultural celebrations. We cover Leicester, Nottingham, Derby, and surrounding areas.",
    customContent: "Leicester is home to one of the UK's most celebrated South Asian communities, especially around Belgrave Road - the Golden Mile. Our dhol players are deeply familiar with Leicester's rich cultural heritage and deliver performances that resonate with local traditions. From Diwali on the Golden Mile to grand wedding receptions, we bring authentic energy to every event."
  },
  {
    slug: "leeds",
    name: "Leeds",
    region: "West Yorkshire",
    county: "West Yorkshire",
    tier: 1,
    areas: ["Leeds City Centre", "Chapeltown", "Harehills", "Beeston", "Headingley", "Roundhay", "Moortown", "Pudsey", "Otley", "Wetherby"],
    venues: ["Queens Hotel Leeds", "The Met Hotel", "Oulton Hall", "Rudding Park", "The Principal Leeds"],
    nearby: ["Bradford", "Huddersfield", "Wakefield", "Halifax", "Sheffield"],
    testimonial: {
      quote: "The dhol performance at our Leeds wedding was absolutely amazing. Professional, punctual, and so talented!",
      author: "Jasmine & Vikram",
      event: "Wedding at Oulton Hall, 2024"
    },
    customIntro: "Searching for dhol players in Leeds? Imperial Drummers provides professional Punjabi dhol entertainment for Asian weddings and events across Yorkshire. We serve Leeds, Bradford, Sheffield, and the entire Yorkshire region.",
    customContent: "Yorkshire's South Asian community is vibrant and diverse, with strong Punjabi, Gujarati, and Bengali populations. Our dhol players understand the unique cultural nuances of Yorkshire celebrations and bring authentic energy to every performance in the region."
  },
  {
    slug: "bradford",
    name: "Bradford",
    region: "West Yorkshire",
    county: "West Yorkshire",
    tier: 1,
    areas: ["Bradford City Centre", "Manningham", "Keighley", "Shipley", "Bingley", "Ilkley", "Saltaire", "Queensbury", "Thornton", "Toller Lane"],
    venues: ["Cedar Court Hotel", "Midland Hotel Bradford", "The Great Victoria Hotel", "Cartwright Hall"],
    nearby: ["Leeds", "Huddersfield", "Halifax", "Dewsbury", "Keighley"],
    testimonial: {
      quote: "Imperial Drummers brought the authentic Bradford wedding experience we wanted. Incredible dhol players!",
      author: "Fatima & Imran",
      event: "Wedding in Bradford, 2024"
    },
    customIntro: "Need dhol players in Bradford? Imperial Drummers serves Bradford and West Yorkshire with professional Punjabi dhol entertainment for Asian weddings, cultural events, and celebrations throughout the region.",
    customContent: "Bradford is one of the UK's most culturally diverse cities with a large and established South Asian community. Our dhol players have extensive experience performing at Bradford weddings and understand the local traditions that make celebrations here so special."
  },

  // ============================================================
  // TIER 2 - Large / Medium Cities
  // ============================================================
  {
    slug: "coventry",
    name: "Coventry",
    region: "West Midlands",
    county: "West Midlands",
    tier: 2,
    areas: ["Coventry City Centre", "Foleshill", "Hillfields", "Earlsdon", "Tile Hill", "Binley", "Nuneaton", "Rugby", "Leamington Spa", "Warwick"],
    venues: ["Coombe Abbey Hotel", "Hilton Coventry", "Village Hotel Coventry", "Warwick Castle"],
    nearby: ["Birmingham", "Leicester", "Northampton", "Rugby", "Warwick"]
  },
  {
    slug: "nottingham",
    name: "Nottingham",
    region: "East Midlands",
    county: "Nottinghamshire",
    tier: 2,
    areas: ["Nottingham City Centre", "Hyson Green", "Radford", "Sneinton", "Beeston", "West Bridgford", "Arnold", "Carlton", "Mansfield", "Newark"],
    venues: ["Colwick Hall Hotel", "Goosedale", "Eastwood Hall", "Nottingham Conference Centre"],
    nearby: ["Leicester", "Derby", "Sheffield", "Mansfield", "Lincoln"]
  },
  {
    slug: "sheffield",
    name: "Sheffield",
    region: "South Yorkshire",
    county: "South Yorkshire",
    tier: 2,
    areas: ["Sheffield City Centre", "Darnall", "Attercliffe", "Ecclesall", "Broomhill", "Rotherham", "Barnsley", "Doncaster", "Chesterfield"],
    venues: ["Kenwood Hall Hotel", "Baldwins Omega", "Halifax Hall", "Mercure Sheffield"],
    nearby: ["Leeds", "Nottingham", "Derby", "Manchester", "Doncaster"]
  },
  {
    slug: "liverpool",
    name: "Liverpool",
    region: "Merseyside",
    county: "Merseyside",
    tier: 2,
    areas: ["Liverpool City Centre", "Toxteth", "Anfield", "Wavertree", "Wirral", "Southport", "St Helens", "Bootle", "Kirkby", "Prescot"],
    venues: ["The Titanic Hotel", "30 James Street", "Crowne Plaza Liverpool", "Liverpool Town Hall"],
    nearby: ["Manchester", "Chester", "Warrington", "Wigan", "Preston"]
  },
  {
    slug: "bristol",
    name: "Bristol",
    region: "South West",
    county: "Bristol",
    tier: 2,
    areas: ["Bristol City Centre", "Easton", "St Pauls", "Clifton", "Bedminster", "Fishponds", "Filton", "Keynsham", "Bath", "Weston-super-Mare"],
    venues: ["Bristol Marriott Royal", "The Grand by Thistle", "Mercure Bristol Grand", "Ashton Gate Stadium"],
    nearby: ["Bath", "Swindon", "Cardiff", "Gloucester", "Cheltenham"]
  },
  {
    slug: "glasgow",
    name: "Glasgow",
    region: "Scotland",
    county: "Glasgow City",
    tier: 2,
    areas: ["Glasgow City Centre", "Pollokshields", "Govanhill", "Ibrox", "Shawlands", "Paisley", "East Kilbride", "Hamilton", "Clydebank", "Motherwell"],
    venues: ["Hilton Glasgow", "Grand Central Hotel", "The Corinthian Club", "200 SVS Glasgow"],
    nearby: ["Edinburgh", "Paisley", "East Kilbride", "Hamilton", "Stirling"]
  },
  {
    slug: "edinburgh",
    name: "Edinburgh",
    region: "Scotland",
    county: "City of Edinburgh",
    tier: 2,
    areas: ["Edinburgh City Centre", "Leith", "Gorgie", "Morningside", "Dalkeith", "Musselburgh", "Livingston", "Dunfermline", "Kirkcaldy"],
    venues: ["The Balmoral", "Edinburgh Castle", "Prestonfield House", "Dalmahoy Hotel"],
    nearby: ["Glasgow", "Dundee", "Stirling", "Livingston", "Dunfermline"]
  },
  {
    slug: "cardiff",
    name: "Cardiff",
    region: "Wales",
    county: "Cardiff",
    tier: 2,
    areas: ["Cardiff City Centre", "Grangetown", "Riverside", "Canton", "Cathays", "Roath", "Newport", "Swansea", "Barry", "Penarth"],
    venues: ["Cardiff City Hall", "Mercure Cardiff Holland House", "Vale Resort", "Marriott Cardiff"],
    nearby: ["Bristol", "Swansea", "Newport", "Bath", "Gloucester"]
  },
  {
    slug: "newcastle",
    name: "Newcastle",
    region: "North East",
    county: "Tyne and Wear",
    tier: 2,
    areas: ["Newcastle City Centre", "Fenham", "Elswick", "Jesmond", "Gosforth", "Gateshead", "Sunderland", "South Shields", "Durham", "Washington"],
    venues: ["Vermont Hotel", "Hilton Newcastle Gateshead", "The Biscuit Factory", "Newcastle Civic Centre"],
    nearby: ["Sunderland", "Durham", "Middlesbrough", "Darlington", "Gateshead"]
  },
  {
    slug: "wolverhampton",
    name: "Wolverhampton",
    region: "West Midlands",
    county: "West Midlands",
    tier: 2,
    areas: ["Wolverhampton City Centre", "Penn", "Tettenhall", "Wednesfield", "Bilston", "Willenhall", "Bloxwich", "Cannock", "Stafford", "Telford"],
    venues: ["Park Hall Hotel", "Wolverhampton Racecourse", "Mount Hotel", "Ramada Park Hall"],
    nearby: ["Birmingham", "Walsall", "Dudley", "Stafford", "Telford"]
  },
  {
    slug: "derby",
    name: "Derby",
    region: "East Midlands",
    county: "Derbyshire",
    tier: 2,
    areas: ["Derby City Centre", "Normanton", "Sinfin", "Chaddesden", "Allestree", "Littleover", "Mickleover", "Spondon", "Chellaston", "Belper"],
    venues: ["Cathedral Quarter Hotel", "Hallmark Hotel Derby", "Morley Hayes", "Breadsall Priory"],
    nearby: ["Nottingham", "Leicester", "Sheffield", "Stoke-on-Trent", "Burton upon Trent"]
  },
  {
    slug: "luton",
    name: "Luton",
    region: "East of England",
    county: "Bedfordshire",
    tier: 2,
    areas: ["Luton Town Centre", "Bury Park", "Farley Hill", "Stopsley", "Leagrave", "Dunstable", "Houghton Regis", "Hitchin", "Stevenage", "Letchworth"],
    venues: ["Luton Hoo Hotel", "Putteridge Bury", "The Shendish Manor", "Aubrey Park Hotel"],
    nearby: ["Stevenage", "Milton Keynes", "Bedford", "St Albans", "Watford"]
  },
  {
    slug: "slough",
    name: "Slough",
    region: "South East",
    county: "Berkshire",
    tier: 2,
    areas: ["Slough Town Centre", "Langley", "Cippenham", "Burnham", "Farnham Royal", "Colnbrook", "Windsor", "Eton", "Maidenhead", "Gerrards Cross"],
    venues: ["Heathrow Windsor Marriott", "Stoke Park Hotel", "De Vere Beaumont Estate", "Copthorne Hotel Slough"],
    nearby: ["London", "Reading", "Watford", "High Wycombe", "Maidenhead"]
  },
  {
    slug: "reading",
    name: "Reading",
    region: "South East",
    county: "Berkshire",
    tier: 2,
    areas: ["Reading Town Centre", "Caversham", "Tilehurst", "Woodley", "Earley", "Wokingham", "Bracknell", "Newbury", "Henley-on-Thames", "Maidenhead"],
    venues: ["The Forbury Hotel", "Hilton Reading", "Wokefield Park", "Nirvana Spa"],
    nearby: ["Slough", "London", "Oxford", "Swindon", "Basingstoke"]
  },
  {
    slug: "southampton",
    name: "Southampton",
    region: "South East",
    county: "Hampshire",
    tier: 2,
    areas: ["Southampton City Centre", "Portswood", "Shirley", "Bitterne", "Totton", "Eastleigh", "Romsey", "Fareham", "Winchester", "Portsmouth"],
    venues: ["Grand Harbour Hotel", "Chilworth Manor", "Carey's Manor Hotel", "Botleigh Grange Hotel"],
    nearby: ["Portsmouth", "Bournemouth", "Winchester", "Basingstoke", "Salisbury"]
  },
  {
    slug: "portsmouth",
    name: "Portsmouth",
    region: "South East",
    county: "Hampshire",
    tier: 2,
    areas: ["Portsmouth City Centre", "Southsea", "Cosham", "Fratton", "Havant", "Gosport", "Fareham", "Waterlooville", "Chichester", "Petersfield"],
    venues: ["Royal Maritime Club", "Portsmouth Guildhall", "Holiday Inn Portsmouth", "Solent Hotel"],
    nearby: ["Southampton", "Bournemouth", "Brighton", "Chichester", "Winchester"]
  },
  {
    slug: "brighton",
    name: "Brighton",
    region: "South East",
    county: "East Sussex",
    tier: 2,
    areas: ["Brighton City Centre", "Hove", "Kemptown", "Rottingdean", "Lewes", "Worthing", "Shoreham", "Burgess Hill", "Haywards Heath", "Crawley"],
    venues: ["The Grand Brighton", "Hilton Brighton Metropole", "Brighton Dome", "Hotel du Vin Brighton"],
    nearby: ["Crawley", "London", "Portsmouth", "Eastbourne", "Worthing"]
  },
  {
    slug: "preston",
    name: "Preston",
    region: "North West",
    county: "Lancashire",
    tier: 2,
    areas: ["Preston Town Centre", "Deepdale", "Fulwood", "Ribbleton", "Ashton-on-Ribble", "Leyland", "Chorley", "Blackburn", "Burnley", "Accrington"],
    venues: ["The Barton Grange Hotel", "Peckforton Castle", "Mercure Preston Samlesbury", "Ribby Hall Village"],
    nearby: ["Blackburn", "Burnley", "Bolton", "Manchester", "Lancaster"]
  },
  {
    slug: "blackburn",
    name: "Blackburn",
    region: "North West",
    county: "Lancashire",
    tier: 2,
    areas: ["Blackburn Town Centre", "Darwen", "Rishton", "Great Harwood", "Accrington", "Clitheroe", "Oswaldtwistle", "Haslingden", "Rawtenstall", "Burnley"],
    venues: ["Dunkenhalgh Hotel", "Mercure Blackburn", "Stanley House Hotel", "Mytton Fold Hotel"],
    nearby: ["Preston", "Burnley", "Bolton", "Manchester", "Rochdale"]
  },

  // ============================================================
  // TIER 3 - Smaller Cities & Towns
  // ============================================================
  {
    slug: "milton-keynes",
    name: "Milton Keynes",
    region: "South East",
    county: "Buckinghamshire",
    tier: 3,
    areas: ["Milton Keynes Centre", "Bletchley", "Wolverton", "Stony Stratford", "Newport Pagnell", "Olney", "Woburn", "Leighton Buzzard", "Aylesbury", "Buckingham"],
    venues: ["Doubletree by Hilton MK", "Venue MK", "Milton Keynes Theatre", "The Cock Hotel"],
    nearby: ["Luton", "Northampton", "Bedford", "Oxford", "Aylesbury"]
  },
  {
    slug: "peterborough",
    name: "Peterborough",
    region: "East of England",
    county: "Cambridgeshire",
    tier: 3,
    areas: ["Peterborough City Centre", "Millfield", "Eastfield", "Paston", "Werrington", "Yaxley", "Stamford", "Market Deeping", "Whittlesey", "Huntingdon"],
    venues: ["Bull Hotel Peterborough", "Orton Hall Hotel", "Peterborough Marriott", "Holiday Inn Peterborough"],
    nearby: ["Leicester", "Northampton", "Cambridge", "Huntingdon", "Stamford"]
  },
  {
    slug: "stevenage",
    name: "Stevenage",
    region: "East of England",
    county: "Hertfordshire",
    tier: 3,
    areas: ["Stevenage Town Centre", "Old Town", "Shephall", "Broadwater", "Pin Green", "Hitchin", "Letchworth", "Baldock", "Welwyn Garden City", "Hatfield"],
    venues: ["Cromwell Hotel", "Knebworth House", "Novotel Stevenage", "Hatfield House"],
    nearby: ["Luton", "Watford", "St Albans", "Cambridge", "London"]
  },
  {
    slug: "macclesfield",
    name: "Macclesfield",
    region: "North West",
    county: "Cheshire",
    tier: 3,
    areas: ["Macclesfield Town Centre", "Bollington", "Prestbury", "Poynton", "Wilmslow", "Alderley Edge", "Congleton", "Knutsford", "Cheadle", "Stockport"],
    venues: ["Shrigley Hall Hotel", "Mottram Hall", "Alderley Edge Hotel", "The Tytherington Club"],
    nearby: ["Manchester", "Stockport", "Stoke-on-Trent", "Chester", "Crewe"]
  },
  {
    slug: "watford",
    name: "Watford",
    region: "South East",
    county: "Hertfordshire",
    tier: 3,
    areas: ["Watford Town Centre", "Bushey", "Rickmansworth", "Chorleywood", "Abbots Langley", "Radlett", "Borehamwood", "Elstree", "Hemel Hempstead", "St Albans"],
    venues: ["The Grove", "Shendish Manor", "Mercure London Watford", "Hunton Park Hotel"],
    nearby: ["London", "St Albans", "Luton", "Hemel Hempstead", "Slough"]
  },
  {
    slug: "northampton",
    name: "Northampton",
    region: "East Midlands",
    county: "Northamptonshire",
    tier: 3,
    areas: ["Northampton Town Centre", "Wellingborough", "Kettering", "Corby", "Rushden", "Daventry", "Towcester", "Brackley", "Desborough", "Rothwell"],
    venues: ["Northampton Marriott", "Highgate House", "Fawsley Hall", "Delapre Abbey"],
    nearby: ["Milton Keynes", "Leicester", "Coventry", "Bedford", "Peterborough"]
  },
  {
    slug: "oxford",
    name: "Oxford",
    region: "South East",
    county: "Oxfordshire",
    tier: 3,
    areas: ["Oxford City Centre", "Cowley", "Headington", "Summertown", "Abingdon", "Witney", "Bicester", "Banbury", "Didcot", "Thame"],
    venues: ["The Randolph Hotel", "Eynsham Hall", "Heythrop Park", "Oxford Thames Four Pillars"],
    nearby: ["Reading", "Milton Keynes", "Swindon", "London", "Banbury"]
  },
  {
    slug: "cambridge",
    name: "Cambridge",
    region: "East of England",
    county: "Cambridgeshire",
    tier: 3,
    areas: ["Cambridge City Centre", "Cherry Hinton", "Arbury", "Trumpington", "Histon", "Ely", "St Neots", "Huntingdon", "Newmarket", "Saffron Walden"],
    venues: ["University Arms Hotel", "Gonville Hotel", "Quy Mill Hotel", "Duxford Lodge"],
    nearby: ["Peterborough", "Stevenage", "Bedford", "London", "Bury St Edmunds"]
  },
  {
    slug: "swindon",
    name: "Swindon",
    region: "South West",
    county: "Wiltshire",
    tier: 3,
    areas: ["Swindon Town Centre", "Old Town", "Wroughton", "Highworth", "Wootton Bassett", "Cricklade", "Marlborough", "Chippenham", "Cirencester", "Devizes"],
    venues: ["De Vere Cotswold Water Park", "Delta by Marriott Swindon", "Stanton Manor Hotel", "Lydiard House"],
    nearby: ["Bristol", "Oxford", "Reading", "Bath", "Gloucester"]
  },
  {
    slug: "stoke-on-trent",
    name: "Stoke-on-Trent",
    region: "West Midlands",
    county: "Staffordshire",
    tier: 3,
    areas: ["Stoke Town Centre", "Hanley", "Burslem", "Tunstall", "Longton", "Fenton", "Newcastle-under-Lyme", "Leek", "Stone", "Cheadle"],
    venues: ["Moat House Hotel", "Best Western Plus Stoke-on-Trent", "Upper House Hotel", "The Potters Club"],
    nearby: ["Macclesfield", "Derby", "Manchester", "Wolverhampton", "Crewe"]
  },
  {
    slug: "huddersfield",
    name: "Huddersfield",
    region: "West Yorkshire",
    county: "West Yorkshire",
    tier: 3,
    areas: ["Huddersfield Town Centre", "Fartown", "Birkby", "Deighton", "Lindley", "Marsden", "Slaithwaite", "Holmfirth", "Dewsbury", "Batley"],
    venues: ["Cedar Court Huddersfield", "The John Smith's Stadium", "Bagden Hall", "Pennine Manor Hotel"],
    nearby: ["Leeds", "Bradford", "Halifax", "Dewsbury", "Manchester"]
  },
  {
    slug: "halifax",
    name: "Halifax",
    region: "West Yorkshire",
    county: "West Yorkshire",
    tier: 3,
    areas: ["Halifax Town Centre", "Pellon", "Ovenden", "Sowerby Bridge", "Elland", "Brighouse", "Todmorden", "Hebden Bridge", "Mytholmroyd", "Ripponden"],
    venues: ["Holdsworth House", "Berties Banqueting Rooms", "Shibden Mill Inn", "Crow Nest Park"],
    nearby: ["Bradford", "Huddersfield", "Leeds", "Dewsbury", "Rochdale"]
  },
  {
    slug: "bolton",
    name: "Bolton",
    region: "Greater Manchester",
    county: "Greater Manchester",
    tier: 3,
    areas: ["Bolton Town Centre", "Farnworth", "Kearsley", "Little Lever", "Horwich", "Westhoughton", "Deane", "Halliwell", "Daubhill", "Astley Bridge"],
    venues: ["Mercure Bolton Georgian House", "Last Drop Village", "Bolton Stadium Hotel", "Rivington Hall"],
    nearby: ["Manchester", "Wigan", "Blackburn", "Preston", "Rochdale"]
  },
  {
    slug: "oldham",
    name: "Oldham",
    region: "Greater Manchester",
    county: "Greater Manchester",
    tier: 3,
    areas: ["Oldham Town Centre", "Chadderton", "Royton", "Shaw", "Lees", "Failsworth", "Glodwick", "Werneth", "Saddleworth", "Uppermill"],
    venues: ["Smokies Park Hotel", "White Hart Inn", "The Saddleworth Hotel", "Hotel Macdonald"],
    nearby: ["Manchester", "Rochdale", "Huddersfield", "Stockport", "Ashton-under-Lyne"]
  },
  {
    slug: "rochdale",
    name: "Rochdale",
    region: "Greater Manchester",
    county: "Greater Manchester",
    tier: 3,
    areas: ["Rochdale Town Centre", "Heywood", "Middleton", "Milnrow", "Littleborough", "Norden", "Castleton", "Bamford", "Whitworth", "Wardle"],
    venues: ["Norton Grange Hotel", "The Hollingworth Lake", "Mercure Manchester Norton Grange", "Rochdale Town Hall"],
    nearby: ["Manchester", "Oldham", "Bolton", "Blackburn", "Halifax"]
  },
  {
    slug: "stockport",
    name: "Stockport",
    region: "Greater Manchester",
    county: "Greater Manchester",
    tier: 3,
    areas: ["Stockport Town Centre", "Cheadle", "Cheadle Hulme", "Bramhall", "Hazel Grove", "Marple", "Reddish", "Edgeley", "Heaton Moor", "Gatley"],
    venues: ["Alma Lodge Hotel", "Hallmark Hotel Manchester Airport", "Styal Lodge", "The Courthouse"],
    nearby: ["Manchester", "Macclesfield", "Oldham", "Wilmslow", "Buxton"]
  },
  {
    slug: "walsall",
    name: "Walsall",
    region: "West Midlands",
    county: "West Midlands",
    tier: 3,
    areas: ["Walsall Town Centre", "Bloxwich", "Aldridge", "Brownhills", "Willenhall", "Darlaston", "Pelsall", "Rushall", "Pheasey", "Streetly"],
    venues: ["Fairlawns Hotel", "Village Hotel Walsall", "Calderfields Golf Club", "The Bescot Stadium"],
    nearby: ["Birmingham", "Wolverhampton", "Dudley", "Lichfield", "Cannock"]
  },
  {
    slug: "dudley",
    name: "Dudley",
    region: "West Midlands",
    county: "West Midlands",
    tier: 3,
    areas: ["Dudley Town Centre", "Stourbridge", "Halesowen", "Brierley Hill", "Sedgley", "Kingswinford", "Cradley Heath", "Old Hill", "Lye", "Amblecote"],
    venues: ["Copthorne Hotel Merry Hill", "The Glynne Arms", "Himley Hall", "Village Hotel Dudley"],
    nearby: ["Birmingham", "Wolverhampton", "Walsall", "Kidderminster", "Sandwell"]
  },
  {
    slug: "burnley",
    name: "Burnley",
    region: "North West",
    county: "Lancashire",
    tier: 3,
    areas: ["Burnley Town Centre", "Padiham", "Brierfield", "Nelson", "Colne", "Barnoldswick", "Clitheroe", "Pendle", "Barrowford", "Trawden"],
    venues: ["Oaks Hotel", "Stirk House Hotel", "Best Western Higher Trapp", "Burnley FC Turf Moor"],
    nearby: ["Blackburn", "Preston", "Halifax", "Rochdale", "Skipton"]
  },
  {
    slug: "wigan",
    name: "Wigan",
    region: "Greater Manchester",
    county: "Greater Manchester",
    tier: 3,
    areas: ["Wigan Town Centre", "Ashton-in-Makerfield", "Leigh", "Hindley", "Standish", "Orrell", "Ince", "Pemberton", "Golborne", "Tyldesley"],
    venues: ["Macdonald Kilhey Court", "Wrightington Hotel", "The DW Stadium", "Park Hall Hotel"],
    nearby: ["Bolton", "Manchester", "Liverpool", "Warrington", "Preston"]
  },
  {
    slug: "warrington",
    name: "Warrington",
    region: "North West",
    county: "Cheshire",
    tier: 3,
    areas: ["Warrington Town Centre", "Birchwood", "Padgate", "Latchford", "Orford", "Woolston", "Penketh", "Great Sankey", "Culcheth", "Lymm"],
    venues: ["Village Hotel Warrington", "The Park Royal Hotel", "Daresbury Park Hotel", "Fir Grove Hotel"],
    nearby: ["Manchester", "Liverpool", "Chester", "Wigan", "Macclesfield"]
  },
  {
    slug: "middlesbrough",
    name: "Middlesbrough",
    region: "North East",
    county: "North Yorkshire",
    tier: 3,
    areas: ["Middlesbrough Town Centre", "Linthorpe", "Acklam", "Marton", "Nunthorpe", "Stockton-on-Tees", "Darlington", "Redcar", "Hartlepool", "Guisborough"],
    venues: ["Judges Country House Hotel", "Rockliffe Hall", "Middlesbrough Town Hall", "Crathorne Hall"],
    nearby: ["Newcastle", "Darlington", "York", "Durham", "Sunderland"]
  },
  {
    slug: "sunderland",
    name: "Sunderland",
    region: "North East",
    county: "Tyne and Wear",
    tier: 3,
    areas: ["Sunderland City Centre", "Roker", "Seaburn", "Washington", "Houghton-le-Spring", "Hetton-le-Hole", "Seaham", "Peterlee", "Chester-le-Street"],
    venues: ["Sunderland Marriott", "Ramside Hall Hotel", "Roker Hotel", "Stadium of Light"],
    nearby: ["Newcastle", "Durham", "Middlesbrough", "Gateshead", "Hartlepool"]
  },
  {
    slug: "york",
    name: "York",
    region: "North Yorkshire",
    county: "North Yorkshire",
    tier: 3,
    areas: ["York City Centre", "Acomb", "Clifton", "Tang Hall", "Heworth", "Haxby", "Strensall", "Bishopthorpe", "Selby", "Tadcaster"],
    venues: ["The Grand York", "The Principal York", "Middlethorpe Hall", "York Racecourse"],
    nearby: ["Leeds", "Sheffield", "Middlesbrough", "Harrogate", "Hull"]
  },
  {
    slug: "bedford",
    name: "Bedford",
    region: "East of England",
    county: "Bedfordshire",
    tier: 3,
    areas: ["Bedford Town Centre", "Kempston", "Bromham", "Clapham", "Elstow", "Wixams", "Sandy", "Biggleswade", "Ampthill", "Flitwick"],
    venues: ["The Swan Hotel", "Bedford Lodge Hotel", "Woodland Manor", "Wyboston Lakes"],
    nearby: ["Milton Keynes", "Luton", "Cambridge", "Northampton", "Stevenage"]
  },
  {
    slug: "gloucester",
    name: "Gloucester",
    region: "South West",
    county: "Gloucestershire",
    tier: 3,
    areas: ["Gloucester City Centre", "Cheltenham", "Tewkesbury", "Stroud", "Dursley", "Cinderford", "Lydney", "Cirencester", "Moreton-in-Marsh", "Stow-on-the-Wold"],
    venues: ["Hatherley Manor Hotel", "Cheltenham Chase Hotel", "Ellenborough Park", "Cowley Manor"],
    nearby: ["Bristol", "Cardiff", "Swindon", "Worcester", "Cheltenham"]
  },
  {
    slug: "exeter",
    name: "Exeter",
    region: "South West",
    county: "Devon",
    tier: 3,
    areas: ["Exeter City Centre", "Heavitree", "Pinhoe", "Exwick", "Topsham", "Crediton", "Tiverton", "Honiton", "Newton Abbot", "Torquay"],
    venues: ["Mercure Exeter Southgate", "Sandy Park Conference Centre", "Reed Hall", "The Hotel Du Vin"],
    nearby: ["Bristol", "Plymouth", "Taunton", "Bournemouth", "Salisbury"]
  },
  {
    slug: "plymouth",
    name: "Plymouth",
    region: "South West",
    county: "Devon",
    tier: 3,
    areas: ["Plymouth City Centre", "Plympton", "Plymstock", "Mutley", "Crownhill", "Devonport", "Saltash", "Tavistock", "Torpoint", "Ivybridge"],
    venues: ["New Continental Hotel", "Duke of Cornwall Hotel", "St Mellion International", "Boringdon Hall"],
    nearby: ["Exeter", "Torquay", "Truro", "Taunton", "Bristol"]
  },
  {
    slug: "norwich",
    name: "Norwich",
    region: "East of England",
    county: "Norfolk",
    tier: 3,
    areas: ["Norwich City Centre", "Thorpe St Andrew", "Hellesdon", "Bowthorpe", "Costessey", "Sprowston", "Great Yarmouth", "Lowestoft", "Dereham", "Thetford"],
    venues: ["Maids Head Hotel", "The Assembly House", "Dunston Hall", "Norfolk Mead Hotel"],
    nearby: ["Cambridge", "Ipswich", "Peterborough", "Colchester", "King's Lynn"]
  },
  {
    slug: "ipswich",
    name: "Ipswich",
    region: "East of England",
    county: "Suffolk",
    tier: 3,
    areas: ["Ipswich Town Centre", "Stowmarket", "Woodbridge", "Felixstowe", "Hadleigh", "Needham Market", "Bury St Edmunds", "Sudbury", "Colchester", "Harwich"],
    venues: ["Milsoms Kesgrave Hall", "Hintlesham Hall", "Best Western Claydon Hotel", "Ufford Park Hotel"],
    nearby: ["Norwich", "Colchester", "Cambridge", "Chelmsford", "London"]
  },
  {
    slug: "chelmsford",
    name: "Chelmsford",
    region: "East of England",
    county: "Essex",
    tier: 3,
    areas: ["Chelmsford City Centre", "Broomfield", "Springfield", "Writtle", "Galleywood", "Boreham", "Maldon", "Witham", "Braintree", "Basildon"],
    venues: ["Channels Estate", "Pontlands Park", "Ivy Hill Hotel", "Greenwoods Hotel"],
    nearby: ["London", "Colchester", "Ipswich", "Southend-on-Sea", "Basildon"]
  },
  {
    slug: "crawley",
    name: "Crawley",
    region: "South East",
    county: "West Sussex",
    tier: 3,
    areas: ["Crawley Town Centre", "Three Bridges", "Ifield", "Bewbush", "Langley Green", "Gatwick", "Horsham", "East Grinstead", "Haywards Heath", "Burgess Hill"],
    venues: ["Crowne Plaza Felbridge", "Cottesmore Hotel", "Arora Hotel Gatwick", "South Lodge Hotel"],
    nearby: ["Brighton", "London", "Guildford", "Croydon", "Horsham"]
  },
  {
    slug: "croydon",
    name: "Croydon",
    region: "Greater London",
    county: "Greater London",
    tier: 3,
    areas: ["Croydon Town Centre", "Thornton Heath", "Norbury", "South Norwood", "Selhurst", "Purley", "Coulsdon", "Addington", "Sanderstead", "Shirley"],
    venues: ["Selsdon Park Hotel", "Hilton Croydon", "Hallmark Hotel Croydon", "The Chameleon"],
    nearby: ["London", "Crawley", "Brighton", "Bromley", "Sutton"]
  },
  {
    slug: "gravesend",
    name: "Gravesend",
    region: "South East",
    county: "Kent",
    tier: 3,
    areas: ["Gravesend Town Centre", "Northfleet", "Meopham", "Istead Rise", "Shorne", "Dartford", "Chatham", "Rochester", "Gillingham", "Strood"],
    venues: ["The Inn on the Lake", "Cobham Hall", "Best Western Manor Hotel", "Gravesend Old Town Hall"],
    nearby: ["London", "Chatham", "Dartford", "Maidstone", "Canterbury"]
  },
  {
    slug: "high-wycombe",
    name: "High Wycombe",
    region: "South East",
    county: "Buckinghamshire",
    tier: 3,
    areas: ["High Wycombe Town Centre", "Marlow", "Beaconsfield", "Amersham", "Chesham", "Princes Risborough", "Bourne End", "Flackwell Heath", "Wooburn Green", "Loudwater"],
    venues: ["Missenden Abbey", "Danesfield House", "De Vere Uplands", "Crowne Plaza Marlow"],
    nearby: ["London", "Slough", "Aylesbury", "Oxford", "Reading"]
  },
  {
    slug: "aylesbury",
    name: "Aylesbury",
    region: "South East",
    county: "Buckinghamshire",
    tier: 3,
    areas: ["Aylesbury Town Centre", "Buckingham", "Wendover", "Thame", "Haddenham", "Waddesdon", "Wing", "Tring", "Leighton Buzzard", "Princes Risborough"],
    venues: ["Waddesdon Manor", "Hartwell House", "The Dairy Waddesdon", "Holiday Inn Aylesbury"],
    nearby: ["Milton Keynes", "High Wycombe", "Oxford", "Luton", "Hemel Hempstead"]
  },
  {
    slug: "st-albans",
    name: "St Albans",
    region: "South East",
    county: "Hertfordshire",
    tier: 3,
    areas: ["St Albans City Centre", "Harpenden", "Redbourn", "London Colney", "Park Street", "Wheathampstead", "Hatfield", "Welwyn Garden City", "Potters Bar", "Radlett"],
    venues: ["Sopwell House", "St Michael's Manor", "Noke Hotel", "Aubrey Park Hotel"],
    nearby: ["Watford", "Luton", "London", "Stevenage", "Hemel Hempstead"]
  },
  {
    slug: "hemel-hempstead",
    name: "Hemel Hempstead",
    region: "South East",
    county: "Hertfordshire",
    tier: 3,
    areas: ["Hemel Hempstead Town Centre", "Apsley", "Adeyfield", "Bennetts End", "Boxmoor", "Leverstock Green", "Berkhamsted", "Tring", "Kings Langley", "Bovingdon"],
    venues: ["Shendish Manor", "Pendley Manor", "Amaravati", "Holiday Inn Hemel Hempstead"],
    nearby: ["Watford", "St Albans", "Luton", "Aylesbury", "Milton Keynes"]
  },
  {
    slug: "basildon",
    name: "Basildon",
    region: "East of England",
    county: "Essex",
    tier: 3,
    areas: ["Basildon Town Centre", "Laindon", "Pitsea", "Wickford", "Rayleigh", "Canvey Island", "Benfleet", "Thurrock", "Grays", "Tilbury"],
    venues: ["Holiday Inn Basildon", "The Chichester Hotel", "Langdon Hills Golf Club", "DoubleTree by Hilton"],
    nearby: ["Chelmsford", "Southend-on-Sea", "London", "Gravesend", "Dartford"]
  },
  {
    slug: "southend-on-sea",
    name: "Southend-on-Sea",
    region: "East of England",
    county: "Essex",
    tier: 3,
    areas: ["Southend Town Centre", "Westcliff-on-Sea", "Leigh-on-Sea", "Shoeburyness", "Thorpe Bay", "Prittlewell", "Canvey Island", "Rayleigh", "Rochford", "Hockley"],
    venues: ["The Roslin Beach Hotel", "The Park Inn by Radisson", "Orsett Hall", "The Lawn"],
    nearby: ["Chelmsford", "Basildon", "London", "Gravesend", "Canterbury"]
  },
  {
    slug: "colchester",
    name: "Colchester",
    region: "East of England",
    county: "Essex",
    tier: 3,
    areas: ["Colchester Town Centre", "Lexden", "Stanway", "Wivenhoe", "Tiptree", "West Mersea", "Witham", "Halstead", "Clacton-on-Sea", "Frinton-on-Sea"],
    venues: ["Wivenhoe House Hotel", "Le Talbooth", "Stoke by Nayland Hotel", "The Marks Tey Hotel"],
    nearby: ["Ipswich", "Chelmsford", "Norwich", "London", "Cambridge"]
  },
  {
    slug: "guildford",
    name: "Guildford",
    region: "South East",
    county: "Surrey",
    tier: 3,
    areas: ["Guildford Town Centre", "Godalming", "Farnham", "Woking", "Cranleigh", "Haslemere", "Dorking", "Leatherhead", "Epsom", "Camberley"],
    venues: ["Guildford Harbour Hotel", "Mandolay Hotel", "Holiday Inn Guildford", "Pennyhill Park"],
    nearby: ["London", "Reading", "Brighton", "Crawley", "Basingstoke"]
  },
  {
    slug: "basingstoke",
    name: "Basingstoke",
    region: "South East",
    county: "Hampshire",
    tier: 3,
    areas: ["Basingstoke Town Centre", "Chineham", "Brighton Hill", "Tadley", "Overton", "Whitchurch", "Hook", "Odiham", "Alton", "Andover"],
    venues: ["Audleys Wood Hotel", "Apollo Hotel", "Hampshire Court Hotel", "The Red Lion Basingstoke"],
    nearby: ["Reading", "Southampton", "Winchester", "London", "Guildford"]
  },
  {
    slug: "bournemouth",
    name: "Bournemouth",
    region: "South West",
    county: "Dorset",
    tier: 3,
    areas: ["Bournemouth Town Centre", "Poole", "Christchurch", "Boscombe", "Winton", "Charminster", "Southbourne", "Westbourne", "Ferndown", "Wimborne Minster"],
    venues: ["Royal Bath Hotel", "The Hilton Bournemouth", "Miramar Hotel", "Compton Acres"],
    nearby: ["Southampton", "Portsmouth", "Salisbury", "Exeter", "Dorchester"]
  },
  {
    slug: "lancaster",
    name: "Lancaster",
    region: "North West",
    county: "Lancashire",
    tier: 3,
    areas: ["Lancaster City Centre", "Morecambe", "Heysham", "Carnforth", "Garstang", "Caton", "Galgate", "Kendal", "Kirkby Lonsdale", "Skipton"],
    venues: ["Lancaster House Hotel", "The Midland Morecambe", "Ashton Memorial", "The Sun Hotel"],
    nearby: ["Preston", "Blackburn", "Kendal", "Carlisle", "Leeds"]
  },
  {
    slug: "chester",
    name: "Chester",
    region: "North West",
    county: "Cheshire",
    tier: 3,
    areas: ["Chester City Centre", "Hoole", "Boughton", "Upton", "Blacon", "Ellesmere Port", "Neston", "Wrexham", "Mold", "Northwich"],
    venues: ["The Chester Grosvenor", "Carden Park Hotel", "Macdonald Craxton Wood", "Doubletree by Hilton Chester"],
    nearby: ["Liverpool", "Manchester", "Warrington", "Wrexham", "Macclesfield"]
  },
  {
    slug: "worcester",
    name: "Worcester",
    region: "West Midlands",
    county: "Worcestershire",
    tier: 3,
    areas: ["Worcester City Centre", "Kidderminster", "Redditch", "Bromsgrove", "Evesham", "Droitwich", "Malvern", "Pershore", "Bewdley", "Stourport-on-Severn"],
    venues: ["The Elms Hotel", "Stanbrook Abbey", "The Wood Norton", "Bank House Hotel"],
    nearby: ["Birmingham", "Gloucester", "Hereford", "Cheltenham", "Coventry"]
  },
  {
    slug: "lincoln",
    name: "Lincoln",
    region: "East Midlands",
    county: "Lincolnshire",
    tier: 3,
    areas: ["Lincoln City Centre", "North Hykeham", "Skellingthorpe", "Waddington", "Bracebridge Heath", "Gainsborough", "Sleaford", "Grantham", "Boston", "Spalding"],
    venues: ["DoubleTree by Hilton Lincoln", "Washingborough Hall", "Branston Hall Hotel", "The Lincoln Hotel"],
    nearby: ["Nottingham", "Sheffield", "Peterborough", "Leicester", "Hull"]
  },
  {
    slug: "hull",
    name: "Hull",
    region: "East Yorkshire",
    county: "East Riding of Yorkshire",
    tier: 3,
    areas: ["Hull City Centre", "Anlaby", "Hessle", "Cottingham", "Willerby", "Beverley", "Brough", "Driffield", "Bridlington", "Goole"],
    venues: ["DoubleTree by Hilton Hull", "Hallmark Hotel Hull", "Lazaat Hotel", "Cave Castle Hotel"],
    nearby: ["York", "Leeds", "Sheffield", "Lincoln", "Grimsby"]
  },
  {
    slug: "swansea",
    name: "Swansea",
    region: "Wales",
    county: "Swansea",
    tier: 3,
    areas: ["Swansea City Centre", "Mumbles", "Sketty", "Uplands", "Brynmill", "Port Talbot", "Neath", "Llanelli", "Gorseinon", "Pontardawe"],
    venues: ["Marriott Hotel Swansea", "The Village Hotel Swansea", "Morgans Hotel", "Towers Hotel"],
    nearby: ["Cardiff", "Bristol", "Newport", "Carmarthen", "Bridgend"]
  },
  {
    slug: "dundee",
    name: "Dundee",
    region: "Scotland",
    county: "Dundee City",
    tier: 3,
    areas: ["Dundee City Centre", "Broughty Ferry", "Lochee", "Menzieshill", "Douglas", "Perth", "Forfar", "Arbroath", "Montrose", "Carnoustie"],
    venues: ["Apex City Quay Hotel", "Malmaison Dundee", "Old Course Hotel St Andrews", "Gleneagles"],
    nearby: ["Edinburgh", "Glasgow", "Perth", "Aberdeen", "Stirling"]
  },
  {
    slug: "aberdeen",
    name: "Aberdeen",
    region: "Scotland",
    county: "Aberdeen City",
    tier: 3,
    areas: ["Aberdeen City Centre", "Torry", "Dyce", "Bridge of Don", "Cove Bay", "Westhill", "Stonehaven", "Inverurie", "Ellon", "Peterhead"],
    venues: ["Marcliffe Hotel", "The Chester Hotel", "Norwood Hall", "Mercure Aberdeen Ardoe House"],
    nearby: ["Dundee", "Edinburgh", "Inverness", "Perth", "Glasgow"]
  },
  {
    slug: "solihull",
    name: "Solihull",
    region: "West Midlands",
    county: "West Midlands",
    tier: 3,
    areas: ["Solihull Town Centre", "Shirley", "Knowle", "Dorridge", "Balsall Common", "Meriden", "Dickens Heath", "Cheswick Green", "Castle Bromwich", "Marston Green"],
    venues: ["The Forest of Arden Marriott", "Hogarths Hotel", "Nuthurst Grange", "The St Johns Hotel"],
    nearby: ["Birmingham", "Coventry", "Warwick", "Stratford-upon-Avon", "Tamworth"]
  },
  {
    slug: "harrow",
    name: "Harrow",
    region: "Greater London",
    county: "Greater London",
    tier: 3,
    areas: ["Harrow Town Centre", "Harrow on the Hill", "Wealdstone", "Stanmore", "Edgware", "Kenton", "Pinner", "Hatch End", "Rayners Lane", "South Harrow"],
    venues: ["The Old Refectory", "Grim's Dyke Hotel", "Best Western Cumberland Hotel", "Harrow School"],
    nearby: ["London", "Watford", "Wembley", "Ruislip", "Uxbridge"]
  },
  {
    slug: "hounslow",
    name: "Hounslow",
    region: "Greater London",
    county: "Greater London",
    tier: 3,
    areas: ["Hounslow Town Centre", "Heston", "Isleworth", "Feltham", "Hanworth", "Brentford", "Chiswick", "Osterley", "Bedfont", "Cranford"],
    venues: ["Osterley Park Hotel", "Holiday Inn London Heathrow", "Syon Park", "The London Hilton Syon Park"],
    nearby: ["London", "Slough", "Ealing", "Richmond", "Heathrow"]
  },
  {
    slug: "southall",
    name: "Southall",
    region: "Greater London",
    county: "Greater London",
    tier: 3,
    areas: ["Southall Broadway", "Norwood Green", "Hayes", "Hanwell", "Greenford", "Northolt", "Ealing", "Acton", "Perivale", "Yeading"],
    venues: ["Grand Sapphire Hotel", "Regency Banqueting Suite", "Royal Nawaab", "The Park Avenue"],
    nearby: ["London", "Hounslow", "Slough", "Harrow", "Ealing"]
  },
  {
    slug: "ilford",
    name: "Ilford",
    region: "Greater London",
    county: "Greater London",
    tier: 3,
    areas: ["Ilford Town Centre", "Seven Kings", "Goodmayes", "Barkingside", "Gants Hill", "Newbury Park", "Hainault", "Chadwell Heath", "Romford", "Dagenham"],
    venues: ["Ilford Banqueting Suite", "The Cranbrook Hotel", "The Boleyn Hotel", "Holiday Inn London Ilford"],
    nearby: ["London", "Romford", "Chelmsford", "Basildon", "Barking"]
  },
  {
    slug: "wembley",
    name: "Wembley",
    region: "Greater London",
    county: "Greater London",
    tier: 3,
    areas: ["Wembley Central", "Wembley Park", "Alperton", "Tokyngton", "Kingsbury", "Preston", "Sudbury", "Neasden", "Harlesden", "Willesden"],
    venues: ["Hilton London Wembley", "London Designer Outlet", "Wembley Stadium", "The SSE Arena"],
    nearby: ["London", "Harrow", "Ealing", "Watford", "Brent"]
  },
  {
    slug: "dewsbury",
    name: "Dewsbury",
    region: "West Yorkshire",
    county: "West Yorkshire",
    tier: 3,
    areas: ["Dewsbury Town Centre", "Batley", "Mirfield", "Heckmondwike", "Cleckheaton", "Birstall", "Gomersal", "Thornhill", "Ravensthorpe", "Soothill"],
    venues: ["Healds Hall Hotel", "The Heath Hotel", "The Victoria Hotel", "Dewsbury Town Hall"],
    nearby: ["Leeds", "Huddersfield", "Bradford", "Halifax", "Wakefield"]
  },
  {
    slug: "wakefield",
    name: "Wakefield",
    region: "West Yorkshire",
    county: "West Yorkshire",
    tier: 3,
    areas: ["Wakefield City Centre", "Ossett", "Horbury", "Normanton", "Castleford", "Pontefract", "Featherstone", "Knottingley", "Stanley", "Outwood"],
    venues: ["Cedar Court Hotel Wakefield", "Waterton Park Hotel", "Wentbridge House", "Holiday Inn Wakefield"],
    nearby: ["Leeds", "Huddersfield", "Dewsbury", "Barnsley", "Doncaster"]
  },
  {
    slug: "doncaster",
    name: "Doncaster",
    region: "South Yorkshire",
    county: "South Yorkshire",
    tier: 3,
    areas: ["Doncaster Town Centre", "Bentley", "Balby", "Bessacarr", "Edenthorpe", "Thorne", "Mexborough", "Conisbrough", "Askern", "Hatfield"],
    venues: ["Doncaster Racecourse", "Mount Pleasant Hotel", "The Earl of Doncaster Hotel", "Best Western Premier"],
    nearby: ["Sheffield", "Leeds", "Nottingham", "Rotherham", "Lincoln"]
  },
  {
    slug: "stafford",
    name: "Stafford",
    region: "West Midlands",
    county: "Staffordshire",
    tier: 3,
    areas: ["Stafford Town Centre", "Stone", "Rugeley", "Cannock", "Lichfield", "Tamworth", "Burton upon Trent", "Uttoxeter", "Eccleshall", "Penkridge"],
    venues: ["The Moat House", "Tillington Hall Hotel", "Stafford Castle", "St Austin's Priory"],
    nearby: ["Wolverhampton", "Birmingham", "Stoke-on-Trent", "Derby", "Telford"]
  },
  {
    slug: "telford",
    name: "Telford",
    region: "West Midlands",
    county: "Shropshire",
    tier: 3,
    areas: ["Telford Town Centre", "Wellington", "Oakengates", "Madeley", "Dawley", "Shifnal", "Newport", "Bridgnorth", "Shrewsbury", "Much Wenlock"],
    venues: ["Telford Hotel & Golf Resort", "Ramada Telford", "Haughton Hall", "Hawkstone Park Hotel"],
    nearby: ["Wolverhampton", "Shrewsbury", "Stafford", "Birmingham", "Chester"]
  },
  {
    slug: "crewe",
    name: "Crewe",
    region: "North West",
    county: "Cheshire",
    tier: 3,
    areas: ["Crewe Town Centre", "Nantwich", "Sandbach", "Alsager", "Middlewich", "Winsford", "Congleton", "Holmes Chapel", "Market Drayton", "Whitchurch"],
    venues: ["Crewe Hall Hotel", "Rookery Hall", "Wychwood Park", "The Crown Hotel Nantwich"],
    nearby: ["Stoke-on-Trent", "Macclesfield", "Chester", "Stafford", "Warrington"]
  },
  {
    slug: "burton-upon-trent",
    name: "Burton upon Trent",
    region: "East Midlands",
    county: "Staffordshire",
    tier: 3,
    areas: ["Burton Town Centre", "Stapenhill", "Winshill", "Stretton", "Branston", "Swadlincote", "Uttoxeter", "Ashby-de-la-Zouch", "Coalville", "Measham"],
    venues: ["The Priory Hotel", "Hoar Cross Hall", "Newton Park Hotel", "The Winery"],
    nearby: ["Derby", "Nottingham", "Leicester", "Stafford", "Birmingham"]
  },
  {
    slug: "maidenhead",
    name: "Maidenhead",
    region: "South East",
    county: "Berkshire",
    tier: 3,
    areas: ["Maidenhead Town Centre", "Cookham", "Marlow", "Bourne End", "Taplow", "Burnham", "Bracknell", "Windsor", "Ascot", "Henley-on-Thames"],
    venues: ["Fredrick's Hotel", "Taplow House Hotel", "Cliveden House", "Stoke Park"],
    nearby: ["Slough", "Reading", "High Wycombe", "London", "Windsor"]
  },
  {
    slug: "stratford-upon-avon",
    name: "Stratford-upon-Avon",
    region: "West Midlands",
    county: "Warwickshire",
    tier: 3,
    areas: ["Stratford Town Centre", "Shottery", "Tiddington", "Wilmcote", "Alcester", "Bidford-on-Avon", "Henley-in-Arden", "Warwick", "Leamington Spa", "Kenilworth"],
    venues: ["The Stratford Hotel", "Ettington Park Hotel", "Alveston Manor", "Chesford Grange"],
    nearby: ["Birmingham", "Coventry", "Warwick", "Worcester", "Oxford"]
  },
];

// Build lookup maps
const cityBySlug = {};
cityDatabase.forEach(city => {
  cityBySlug[city.slug] = city;
});

// ============================================================
// Template generators for cities without custom copy
// ============================================================
const generateIntro = (city) => {
  if (city.customIntro) return city.customIntro;
  return `Looking for dhol players in ${city.name}? Imperial Drummers provides professional Punjabi dhol entertainment for Asian weddings, Indian weddings, and cultural celebrations across ${city.name} and ${city.region}. Our experienced dhol drummers are available for hire throughout ${city.county} and surrounding areas.`;
};

const generateContent = (city) => {
  if (city.customContent) return city.customContent;
  return `Our professional dhol players have performed at hundreds of events across ${city.region}, bringing authentic Punjabi rhythms and energy to every celebration. Whether you're planning a traditional Sikh wedding, a Hindu ceremony, or a modern fusion event in ${city.name}, Imperial Drummers delivers a performance your guests will remember. We work closely with local wedding planners and venues throughout ${city.county} to ensure your entertainment is perfectly coordinated.`;
};

const generateMetaTitle = (city) => {
  if (city.customMetaTitle) return city.customMetaTitle;
  return `Dhol Players ${city.name} | Professional Dhol Drummers ${city.region} | Imperial Drummers`;
};

const generateMetaDescription = (city) => {
  if (city.customMetaDescription) return city.customMetaDescription;
  // Name two real local areas so each city's snippet differs beyond the city
  // name. areas[0] is usually the centre ("X City Centre"), so start at [1].
  const localAreas = (city.areas || []).slice(1, 3);
  const coverage = localAreas.length === 2
    ? `${localAreas[0]}, ${localAreas[1]} & across ${city.county}`
    : `across ${city.county}`;
  return `Book dhol players in ${city.name} for weddings, baraats, mehndis & Vaisakhi. Punjabi dhol drummers in ${coverage}. Free quote.`;
};

// Only real, attributable reviews. The generic "Happy Clients" placeholder
// repeated identical text on every city page, which is exactly the templated
// content that holds these pages back; a city without a review shows none.
const generateReviews = (city) => [
  ...(city.testimonial ? [city.testimonial] : []),
  ...(city.reviews || []),
];

const generateFaqs = (city) => {
  if (city.faqs) return city.faqs;
  return [
    {
      question: `How much do dhol players cost in ${city.name}?`,
      answer: city.pricing
        ? `Dhol players in ${city.name} start from ${city.pricing.from}${city.pricing.note ? ` (${city.pricing.note})` : ""}. The final price depends on the number of musicians, performance duration, and event type. Contact Imperial Drummers for a personalised quote.`
        : `Dhol player pricing in ${city.name} varies based on the number of musicians, performance duration, and event type. Contact Imperial Drummers for a personalised quote tailored to your ${city.name} celebration.`
    },
    {
      question: `How far in advance should I book dhol players in ${city.name}?`,
      answer: `We recommend booking at least 3–6 months in advance for weddings in ${city.name}, particularly during peak season (April–September). Last-minute bookings are accommodated subject to availability.`
    },
    {
      question: `Do you cover all areas of ${city.region}?`,
      answer: `Yes, Imperial Drummers provides dhol entertainment throughout ${city.name} and the wider ${city.region} area, including surrounding towns and suburbs. Travel costs may apply for venues outside ${city.name} city centre.`
    },
    {
      question: `What events do your ${city.name} dhol players perform at?`,
      answer: `Our dhol players perform at Asian weddings, baraat processions, Sikh and Hindu ceremonies, corporate events, Diwali celebrations, mehndi nights, birthday parties, and any cultural celebration in ${city.name} requiring authentic Punjabi dhol entertainment.`
    }
  ];
};

// ============================================================
// Public API
// ============================================================

export const getCityBySlug = (slug) => {
  const city = cityBySlug[slug];
  if (!city) return null;

  // Return enriched city with generated fields
  return {
    ...city,
    metaTitle: generateMetaTitle(city),
    metaDescription: generateMetaDescription(city),
    h1: city.customH1 || `Professional Dhol Players in ${city.name}`,
    intro: generateIntro(city),
    localContent: generateContent(city),
    reviews: generateReviews(city),
    faqs: generateFaqs(city),
  };
};

export const getAllCities = () =>
  cityDatabase.map(city => ({
    ...city,
    metaTitle: generateMetaTitle(city),
    metaDescription: generateMetaDescription(city),
    h1: city.customH1 || `Professional Dhol Players in ${city.name}`,
    intro: generateIntro(city),
    localContent: generateContent(city),
    reviews: generateReviews(city),
  }));

// For footer / navigation – grouped by region
export const getCitiesByRegion = () => {
  const regions = {};
  cityDatabase.forEach(city => {
    if (!regions[city.region]) regions[city.region] = [];
    regions[city.region].push({ name: city.name, slug: city.slug });
  });
  return regions;
};

// For sitemap generation
export const getAllSlugs = () => cityDatabase.map(c => c.slug);
